export type WidgetView = 'list' | 'chart';
export type WidgetItemLimitView = 'list' | 'chart';

// This order is the dashboard's shared contract: initial fetch order, card
// layout, and generated widget state all use it.
export const DASHBOARD_QUERIES = [
  'active_patient_census',
  'case_manager_queue',
  'outstanding_oasis',
  'past_due_tasks',
  'census_by_primary_insurer',
  'orders_pending_signature',
  'census_by_primary_diagnosis',
] as const;

export type DashboardQuery = (typeof DASHBOARD_QUERIES)[number];

export const DASHBOARD_TITLES: Record<DashboardQuery, string> = {
  active_patient_census: 'Active Patient Census',
  case_manager_queue: 'Case Manager Queue',
  outstanding_oasis: 'Outstanding OASIS',
  past_due_tasks: 'Past Due Tasks',
  census_by_primary_insurer: 'Census by Primary Insurer',
  orders_pending_signature: 'Orders Pending Signature',
  census_by_primary_diagnosis: 'Census by Primary Diagnosis',
};

// Only widgets that can switch between list and chart belong here. Widgets
// without a view choice stay out of the preference payload.
export const DEFAULT_DASHBOARD_VIEWS = {
  case_manager_queue: 'list',
  past_due_tasks: 'list',
  census_by_primary_insurer: 'chart',
  census_by_primary_diagnosis: 'list',
} as const satisfies Partial<Record<DashboardQuery, WidgetView>>;

export const DEFAULT_LIST_ITEM_LIMIT = 20;
export const DEFAULT_CHART_ITEM_LIMIT = 8;

// These limits are persisted per view so a chart can stay compact without
// forcing the matching list to hide useful rows.
export const DEFAULT_WIDGET_ITEM_LIMITS = {
  case_manager_queue: {
    list: DEFAULT_LIST_ITEM_LIMIT,
    chart: DEFAULT_CHART_ITEM_LIMIT,
  },
  past_due_tasks: {
    list: DEFAULT_LIST_ITEM_LIMIT,
    chart: DEFAULT_CHART_ITEM_LIMIT,
  },
  census_by_primary_insurer: {
    list: DEFAULT_LIST_ITEM_LIMIT,
    chart: DEFAULT_CHART_ITEM_LIMIT,
  },
  census_by_primary_diagnosis: {
    list: DEFAULT_LIST_ITEM_LIMIT,
    chart: DEFAULT_CHART_ITEM_LIMIT,
  },
} as const satisfies Partial<
  Record<DashboardQuery, Record<WidgetItemLimitView, number>>
>;

export type WidgetViewPreferenceKey = keyof typeof DEFAULT_DASHBOARD_VIEWS;
export type WidgetItemLimitKey = keyof typeof DEFAULT_WIDGET_ITEM_LIMITS;
export type WidgetItemLimits = Record<WidgetItemLimitView, number>;

export const VIEW_PREFERENCE_WIDGETS = Object.keys(
  DEFAULT_DASHBOARD_VIEWS
) as WidgetViewPreferenceKey[];

export function isViewPreferenceWidget(
  query: DashboardQuery
): query is WidgetViewPreferenceKey {
  return query in DEFAULT_DASHBOARD_VIEWS;
}

export function isItemLimitWidget(
  query: DashboardQuery
): query is WidgetItemLimitKey {
  return query in DEFAULT_WIDGET_ITEM_LIMITS;
}
